import { memo, useEffect, useRef, useState } from 'react';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import { useTranslation } from 'react-i18next';
import type { ConvMessage } from '../types';
import styles from './conversation.module.css';

export interface QuestionItemProps {
  message: ConvMessage;
  number: number | null;
  /** The number of the question it edits. */
  parentNumber: number | null;
  busy: boolean;
  editing: boolean;
  /** What an edited resubmission is sent with (mode, sources, prompt). */
  editConditions: string;
  onStartEdit: (message: ConvMessage) => void;
  onCancelEdit: () => void;
  /** Resolves to whether the resubmission was sent. */
  onResubmit: (message: ConvMessage, text: string) => Promise<boolean>;
  onCopyFailed: (text: string) => void;
}

/** The documents the question was sent with (`source_record`). */
const Sources = ({ message }: { message: ConvMessage }) => {
  const { t } = useTranslation();
  if (message.local === 'pending') return null;
  const record = message.source_record;
  if (
    record?.origin === 'chat_only' ||
    (!record && message.mode === 'chat_only')
  ) {
    return (
      <span className={styles.caption} data-testid="question-sources">
        {t('v2.messages.noDocsSent')}
      </span>
    );
  }
  if (!record || !record.files) {
    return (
      <span className={styles.caption} data-testid="question-sources">
        {t('v2.common.labelValue', {
          label: t('v2.messages.sources'),
          value: t('v2.common.notRecorded'),
        })}
      </span>
    );
  }
  const excluded = record.excluded ?? [];
  return (
    <details className={styles.details} data-testid="question-sources">
      <summary>
        {t('v2.selection.snapshot', { count: record.files.length })}
      </summary>
      <ul>
        {record.files.map(file => (
          <li key={file.id}>{file.name ?? t('v2.common.notRecorded')}</li>
        ))}
        {excluded.map(file => (
          <li key={`x${file.id}`} className={styles.excluded}>
            {file.name ?? t('v2.common.notRecorded')}
            {t('v2.common.paren', { text: t('v2.messages.notSent') })}
          </li>
        ))}
      </ul>
    </details>
  );
};

/** The per-Q&A prompt in effect when it was sent. */
const Prompt = ({ message }: { message: ConvMessage }) => {
  const { t } = useTranslation();
  if (message.local === 'pending') return null;
  const label = t('v2.prompt.promptLabel');
  if (message.prompt_label === 'custom' && message.custom_prompt) {
    return (
      <details className={styles.details} data-testid="question-prompt">
        <summary>
          {t('v2.common.labelValue', {
            label,
            value: t('v2.prompt.promptLabelCustom'),
          })}
        </summary>
        <p className={styles.promptText}>{message.custom_prompt}</p>
      </details>
    );
  }
  const value =
    message.prompt_label === 'default'
      ? t('v2.prompt.promptLabelDefault')
      : message.prompt_label === 'custom'
        ? t('v2.prompt.promptLabelCustom')
        : t('v2.common.notRecorded');
  return (
    <span className={styles.caption} data-testid="question-prompt">
      {t('v2.common.labelValue', { label, value })}
    </span>
  );
};

/** A question, right-aligned, with what it was sent with (spec §3). */
const QuestionItem = ({
  message,
  number,
  parentNumber,
  busy,
  editing,
  editConditions,
  onStartEdit,
  onCancelEdit,
  onResubmit,
  onCopyFailed,
}: QuestionItemProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(message.message);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) {
      setDraft(message.message);
      requestAnimationFrame(() => field.current?.focus());
    }
  }, [editing, message.message]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.message);
      setCopied(true);
    } catch {
      onCopyFailed(message.message);
    }
  };

  const submit = async () => {
    if (!draft.trim() || busy || sending) return;
    setSending(true);
    const sent = await onResubmit(message, draft);
    setSending(false);
    if (sent) onCancelEdit();
  };

  return (
    <div
      className={styles.questionItem}
      data-testid="chat-bubble-question"
      data-message-id={message.id}
    >
      {message.kind === 'edit' ? (
        <span className={styles.caption} data-testid="question-kind">
          {t('v2.messages.editedFrom', { n: parentNumber ?? '—' })}
        </span>
      ) : null}
      {editing ? (
        <form
          className={styles.editForm}
          onSubmit={event => {
            event.preventDefault();
            void submit();
          }}
        >
          <textarea
            ref={field}
            className={styles.editField}
            aria-label={t('v2.messages.editResubmit')}
            value={draft}
            rows={3}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Escape') {
                event.stopPropagation();
                onCancelEdit();
              }
            }}
          />
          <p className={styles.caption}>{editConditions}</p>
          <div className={styles.editButtons}>
            <button
              type="button"
              className={styles.action}
              onClick={onCancelEdit}
            >
              {t('v2.common.cancel')}
            </button>
            <button
              type="submit"
              className={styles.primaryAction}
              data-testid="question-resubmit"
              disabled={!draft.trim() || busy || sending}
            >
              {t('v2.selection.resubmit')}
            </button>
          </div>
        </form>
      ) : (
        <div
          className={styles.question}
          data-testid="question-text"
          aria-label={
            number != null
              ? t('v2.messages.questionN', { n: number })
              : undefined
          }
        >
          {message.message}
        </div>
      )}
      <div className={styles.questionMeta}>
        <Sources message={message} />
        <Prompt message={message} />
        <button
          type="button"
          className={styles.iconAction}
          aria-label={copied ? t('v2.messages.copied') : t('v2.messages.copy')}
          title={copied ? t('v2.messages.copied') : t('v2.messages.copy')}
          onClick={() => void copy()}
        >
          {copied ? (
            <CheckOutlined aria-hidden />
          ) : (
            <CopyOutlined aria-hidden />
          )}
        </button>
        {message.local !== 'removed' ? (
          <button
            type="button"
            className={styles.iconAction}
            aria-label={t('v2.messages.editResubmit')}
            title={t('v2.messages.editResubmit')}
            data-testid="question-edit"
            disabled={busy || editing}
            onClick={() => onStartEdit(message)}
          >
            <EditOutlined aria-hidden />
          </button>
        ) : null}
        {copied ? (
          <span role="status" className={styles.copied}>
            {t('v2.messages.copied')}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default memo(QuestionItem);
